<script setup lang="ts">
  import ModalQC from '@qicgroupdoha/qic-connect-ds/components/ModalQC'
  import { type Plan, SchemeType } from '@/shared/types/api.types'
  import type {
    Coverage,
    CoverageOption,
  } from '@/entities/Flow/components/FlowPlanCoverage/FlowPlanCoverage.types'
  import IconQC from '@qicgroupdoha/qic-connect-ds/components/IconQC'
  import { useI18n } from 'vue-i18n'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import { POLICY_WORDING_CLICK_EVENT } from '@/entities/Flow/components/FlowPlan/FlowPlan.constants'
  import { computed, onMounted } from 'vue'
  import { PLAN_NAMES_SCHEME_TYPE_CODE_MAP } from '@/shared/constants/plans.constants'
  import { useGoogleAnalytics } from '@/shared/composables'
  import { useRoute } from 'vue-router'
  import type { AvailableFlowRoutes } from '@/shared/types/flow.types'
  import type { Locale } from '@/shared/types/common.types'

  interface Props {
    plansSchemeTypeCodes: Plan['schemeTypeCode'][]
    dataTest?: string
  }

  interface Emits {
    (e: 'close'): void

    (e: typeof POLICY_WORDING_CLICK_EVENT): void
  }

  withDefaults(defineProps<Props>(), {
    dataTest: 'learn-more-modal',
  })

  const emit = defineEmits<Emits>()

  const modelValue = defineModel<SchemeType>()

  const { t, locale } = useI18n()
  const gaService = useGoogleAnalytics()
  const route = useRoute()

  const healthCoverage = computed<CoverageOption[]>(() => [
    {
      iconName: 'heart-pulse',
      name: t('outbound.select_plan.insurance_values.category_1.item_1.name'),
      price:
        modelValue.value === SchemeType.FlyEurope
          ? t('outbound.select_plan.insurance_values.category_1.item_1.desc_flyeurope')
          : t('outbound.select_plan.insurance_values.category_1.item_1.desc'),
      tooltip: t('outbound.select_plan.insurance_values.category_1.item_1.tooltip_desc'),
      popup: [
        [
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_1_1'),
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_1_2'),
        ],
        [
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_2_1'),
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_2_2'),
        ],
        [
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_3_1'),
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_3_2'),
        ],
        [
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_4_1'),
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_4_2'),
        ],
        [
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_5_1'),
          t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc_5_2'),
        ],
      ],
    },
    {
      iconName: 'teeth',
      name: t('outbound.select_plan.insurance_values.category_1.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_1.item_2.desc'),
      popup: [[t('outbound.select_plan.insurance_values.category_1.item_1.pop_desc')]],
    },
    {
      iconName: 'siren',
      name: t('outbound.select_plan.insurance_values.category_1.item_2_1.name'),
      price: t('outbound.select_plan.insurance_values.category_1.item_2_1.desc'),
      popup: [[t('outbound.select_plan.insurance_values.category_1.item_2_1.pop_desc')]],
    },
    {
      iconName: 'personal-damage-2',
      name: t('outbound.select_plan.insurance_values.category_1.item_2_2.name'),
      price: t('outbound.select_plan.insurance_values.category_1.item_2_2.desc'),
      popup: [[t('outbound.select_plan.insurance_values.category_1.item_2_2.pop_desc')]],
      notAvailableFor: [SchemeType.FlyEurope],
    },
    {
      iconName: 'skiing',
      name: t('outbound.select_plan.insurance_values.category_1.item_3.name'),
      price: t('outbound.select_plan.insurance_values.addon'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_1.item_3.pop_desc')]],
    },
    {
      iconName: 'virus',
      name: t('outbound.select_plan.insurance_values.category_1.item_4.name'),
      price: t('outbound.select_plan.insurance_values.addon'),
      notAvailableFor: [SchemeType.Regional, SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_1.item_4.pop_desc')]],
    },
  ])

  const belongingsCoverage = computed<CoverageOption[]>(() => [
    {
      iconName: 'luggage',
      name: t('outbound.select_plan.insurance_values.category_2.item_1.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_2.item_1.pop_desc')]],
    },
    {
      iconName: 'luggage',
      name: t('outbound.select_plan.insurance_values.category_2.item_1_1.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_1_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_2.item_1_1.pop_desc')]],
    },
    {
      iconName: 'passport',
      name: t('outbound.select_plan.insurance_values.category_2.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_2.item_2.pop_desc')]],
    },
    {
      iconName: 'bicycle',
      name: t('outbound.select_plan.insurance_values.category_2.item_3.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_3.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_2.item_3.pop_desc')]],
    },
    {
      iconName: 'bicycle',
      name: t('outbound.select_plan.insurance_values.category_2.item_3_1.name'),
      price: t('outbound.select_plan.insurance_values.category_2.item_3_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_2.item_3_1.pop_desc')]],
    },
  ])

  const tripCoverage = computed<CoverageOption[]>(() => [
    {
      iconName: 'plane',
      name: t('outbound.select_plan.insurance_values.category_3.item_1_1.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_1_1.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_3.item_1_1.pop_desc')]],
    },
    {
      iconName: 'plane',
      name: t('outbound.select_plan.insurance_values.category_3.item_1_2.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_1_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_3.item_1_2.pop_desc')]],
    },
    {
      iconName: 'shield-user',
      name: t('outbound.select_plan.insurance_values.category_3.item_2.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_3.item_2.pop_desc')]],
    },
    {
      iconName: 'scales',
      name: t('outbound.select_plan.insurance_values.category_3.item_3.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_3.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_3.item_3.pop_desc')]],
    },
    {
      iconName: 'clock',
      name: t('outbound.select_plan.insurance_values.category_3.item_4.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_4.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_3.item_4.pop_desc')]],
    },
    {
      iconName: 'heart',
      name: t('outbound.select_plan.insurance_values.category_3.item_4_1.name'),
      price: t('outbound.select_plan.insurance_values.category_3.item_4_2.desc'),
      notAvailableFor: [SchemeType.FlyEurope],
      popup: [[t('outbound.select_plan.insurance_values.category_3.item_4_3.pop_desc')]],
    },
  ])

  const coverage = computed<Coverage[]>(() => [
    {
      label: t('outbound.select_plan.insurance_values.category_1.label'),
      options: healthCoverage.value,
    },
    {
      label: t('outbound.select_plan.insurance_values.category_2.label'),
      options: belongingsCoverage.value,
    },
    {
      label: t('outbound.select_plan.insurance_values.category_3.label'),
      options: tripCoverage.value,
    },
  ])

  const handleTabClick = () => {
    const planNameEventMap = {
      [SchemeType.Worldwide]: 'total_protection_usa',
      [SchemeType.WorldwideExcl]: 'total_protection',
      [SchemeType.Regional]: 'regional',
      [SchemeType.FlyEurope]: 'basic',
    }

    const tab = planNameEventMap[modelValue.value!]
    gaService.sendPlanTabClick(route.name as AvailableFlowRoutes, tab)
  }

  const handlePolicyWordingsClick = () => {
    emit(POLICY_WORDING_CLICK_EVENT)
    gaService.sendOutboundPlanDetailsPolicyWordings()
  }

  onMounted(() => {
    gaService.sendPlanDetailsPopupOpened(route.name as AvailableFlowRoutes)
  })
</script>

<template>
  <ModalQC
    :title="$t('outbound.select_plan.insurance_values.pop_up.header')"
    hide-controls
    position="side"
    :locale="locale as Locale"
    @close="emit('close')"
  >
    <template #content>
      <div class="popup-coverage">
        <div v-if="plansSchemeTypeCodes.length > 1" class="popup-coverage__slider">
          <template v-for="(code, index) in plansSchemeTypeCodes" :key="code">
            <input
              :id="`option${index}`"
              v-model="modelValue"
              :data-test="`${dataTest}-slider-input-${index}`"
              type="radio"
              name="toggle"
              :value="code"
              @change="handleTabClick"
            />
            <label
              :for="`option${index}`"
              :data-test="`${dataTest}-slider-label-${index}`"
              :class="[
                'popup-coverage__slider-element',
                { 'popup-coverage__slider-element--active': modelValue === code },
              ]"
              >{{ $t(PLAN_NAMES_SCHEME_TYPE_CODE_MAP[code]) }}</label
            >
          </template>
        </div>
        <div class="popup-coverage__content-scrollable">
          <template v-for="{ label, options } in coverage" :key="label">
            <div
              v-if="!options.every((option) => option.notAvailableFor?.includes(modelValue!))"
              class="popup-coverage__coverage-card"
            >
              <div class="popup-coverage__coverage-card-title">
                {{ label }}
              </div>
              <template v-for="option in options" :key="option.name">
                <div
                  v-if="!option?.notAvailableFor?.includes(modelValue!)"
                  class="popup-coverage__coverage-option"
                >
                  <div class="popup-coverage__option-header">
                    <div class="popup-coverage__option-title-wrapper">
                      <IconQC :name="option.iconName" color="#394B5ACC" :size="20" />
                      <div class="popup-coverage__option-title">{{ option.name }}</div>
                    </div>
                    <div class="popup-coverage__option-price">{{ option.price }}</div>
                  </div>
                  <ul class="popup-coverage__option-descriptions">
                    <li
                      v-for="(description, index) in option.popup"
                      :key="index"
                      class="popup-coverage__option-description"
                    >
                      <div class="popup-coverage__option-description-wrapper">
                        <span v-for="descriptionPart in description" :key="descriptionPart">{{
                          descriptionPart
                        }}</span>
                      </div>
                    </li>
                  </ul>
                </div>
              </template>
            </div>
          </template>
        </div>
        <div class="popup-coverage__footer-buttons">
          <ButtonQC variant="ghost" wide icon="file" @click="handlePolicyWordingsClick">
            <span>{{ $t('outbound.plans.plan_information.policy_wording') }}</span>
          </ButtonQC>
          <ButtonQC variant="secondary" wide @click="emit('close')"
            >{{ $t('qic.close') }}
          </ButtonQC>
        </div>
      </div>
    </template>
  </ModalQC>
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/utils' as *;
  @use '@qicgroupdoha/qic-connect-ds/scss/typography';

  .popup-coverage {
    display: flex;
    flex-direction: column;
    max-height: 86vh;
    gap: var(--spacing-6);

    &__content-scrollable {
      overflow-y: auto;
      flex: 1 1 auto;
      padding-inline-end: var(--spacing-2);
      min-height: 0;
      display: flex;
      flex-direction: column;
      gap: var(--spacing-5);

      @include mobile-flow {
        padding-inline-end: 0;
      }
    }

    &__slider {
      position: relative;
      display: flex;
      padding: var(--spacing-2);
      border-radius: var(--radius-4);

      /* qic-connect-ignore-next-line */
      background-color: #f7f7fc;

      /* qic-connect-ignore-next-line */
      margin-inline-end: 19px;
      color: var(--colorTxtBtnOutlineDefault);

      @include typography.text-medium;

      @include mobile-flow {
        margin-inline-end: 0;
        flex: 0 0 auto;
      }
    }

    &__slider-element {
      flex: 1;
      min-height: 56px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      align-self: stretch;
      cursor: pointer;
      text-align: center;
      padding: 0 var(--spacing-4);
      white-space: pre-wrap;

      &--active {
        border-radius: var(--radius-4);
        background: var(--colorBgBtnGhostFocus);
        box-shadow: 0 2px 8px 0 rgb(0 0 0 / 10%);
        color: var(--colorTxtBtnGhostDefault);
        transition: transform 1s ease;
      }

      & > input {
        display: none;
      }

      & > label {
        cursor: pointer;
        z-index: 1;
      }
    }

    &__coverage-card {
      display: flex;
      padding: var(--spacing-7);
      flex-direction: column;

      /* qic-connect-ignore-next-line */
      gap: -1px;
      align-self: stretch;
      border-radius: var(--radius-7);

      /* qic-connect-ignore-next-line */
      background: #f7f7fc;

      @include mobile-flow {
        padding: var(--spacing-7) var(--spacing-5);
      }
    }

    &__coverage-card-title {
      /* qic-connect-ignore-next-line */
      color: #2e2c3a;

      @include typography.heading-3;

      @include mobile-flow {
        @include typography.heading-4;
      }
    }

    &__coverage-option {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      align-self: stretch;

      &::before {
        content: '';
        width: 100%;
        height: 1px;

        /* qic-connect-ignore-next-line */
        background: #e5e3ff;
        margin-block: 16px;
      }
    }

    &__option-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--spacing-5);
      align-self: stretch;
      padding-bottom: 4px;
    }

    &__option-title-wrapper {
      display: flex;
      gap: 16px;

      & > svg {
        flex-shrink: 0;
      }
    }

    &__option-title {
      color: var(--colorTxtBtnOutlineDefault);

      @include typography.extra-bonus-small-semibold;
    }

    &__option-price {
      color: var(--colorTxtBtnGhostDefault);
      text-align: right;

      @include typography.text-small-medium;

      @include mobile-flow {
        flex-shrink: 0;
      }
    }

    &__option-descriptions {
      display: flex;
      padding-inline-start: 37px;
      flex-direction: column;
      align-items: flex-start;
      gap: var(--spacing-2);
      align-self: stretch;
    }

    &__option-description {
      @include typography.text-small-regular;

      display: flex;
      align-items: flex-start;
      gap: 4px;
      align-self: stretch;

      &:has(.popup-coverage__option-description-wrapper > span:nth-child(2))::before {
        content: '';
        display: inline-block;
        width: 4px;
        height: 4px;

        /* qic-connect-ignore-next-line */
        background-color: #2e2c3a;

        /* qic-connect-ignore-next-line */
        border-radius: 50%;
        margin-inline: 4px 8px;
        margin-block-start: 10px;
        flex-shrink: 0;
      }

      & span:first-child {
        @include typography.text-small-medium;
      }
    }

    &__footer-buttons {
      display: flex;
      flex: 0 0 auto;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      align-self: stretch;
    }

    &__footer-text {
      color: var(--colorTxtBtnGhostDefault);
      text-align: center;

      @include typography.extra-bonus-default-medium;
    }

    &__button--link {
      display: flex;
      gap: 12px;
      width: auto;
      padding: 0;
    }
  }

  :deep(.modal__content) {
    max-width: 544px;

    @include mobile-flow {
      padding: 24px 12px 12px;
      max-height: calc(100vh - 32px);
      overflow: hidden;
    }
  }
</style>
